/**
 * Generates an array of page numbers and dividers for pagination display.
 *
 * The function creates a pagination array with the following structure:
 * - Leading edge pages (1 to edgeCount)
 * - A divider or additional page number if needed
 * - Middle pages around the current page (currentPage ± siblingCount)
 * - A divider or additional page number if needed
 * - Trailing edge pages (totalPages-edgeCount+1 to totalPages)
 *
 * @param currentPage - The current active page.
 * @param totalPages - The total number of pages.
 * @param edgeCount - The number of pages to show at the beginning and end.
 * @param siblingCount - The number of pages to show around the current page.
 * @returns An array of page numbers and dividers.
 * @throws If parameters are invalid (totalPages ≤ 1, currentPage out of range, negative counts).
 */
function generatePaginationPagesArray({ currentPage, totalPages, edgeCount, siblingCount, }) {
    // Validate input parameters
    if (totalPages <= 1) {
        throw new Error("Total pages must be greater than 1");
    }
    if (edgeCount < 0 || siblingCount < 0) {
        throw new Error("Edge count and middle count must be greater than or equal to 0");
    }
    // Calculate leading page numbers (at the beginning of pagination)
    // These always start with page 1 and include up to `edgeCount` pages
    const leadingStart = 1;
    const leadingEnd = Math.min(edgeCount, totalPages);
    const leadingPart = range(leadingStart, leadingEnd);
    // Calculate trailing page numbers (at the end of pagination)
    // These always end with the last page and include up to `edgeCount` pages
    const trailingStart = Math.max(totalPages - edgeCount + 1, edgeCount + 1);
    const trailingEnd = totalPages;
    const trailingPart = range(trailingStart, trailingEnd);
    // Calculate the middle section of page numbers around the current page
    // middleStart: The first page in the middle section, ensuring it doesn't overlap with leading pages
    // and stays within the appropriate range considering current page and sibling count
    const middleStart = Math.max(2 + edgeCount, // Don't overlap with leading pages
    Math.min(currentPage - siblingCount, // Start `siblingCount` pages before current page
    totalPages - edgeCount - siblingCount * 2 - 1 // Ensure proper spacing from trailing pages
    ));
    // middleEnd: The last page in the middle section, ensuring it doesn't overlap with trailing pages
    // and includes enough pages around the current page based on siblingCount
    const middleEnd = Math.min(totalPages - edgeCount - 1, // Don't overlap with trailing pages
    Math.max(currentPage + siblingCount, // End `siblingCount` pages after current page
    edgeCount + siblingCount * 2 + 2 // Ensure enough pages in the middle section
    ));
    // Generate the middle part array using the calculated bounds
    const middlePart = range(middleStart, middleEnd);
    // Determine if we need a divider (or an extra page) between leading and middle sections
    const leadingDivider = middleStart > edgeCount + 2
        ? "leading-divider" // Add divider if there's a gap between leading and middle sections
        : leadingEnd + 1 < totalPages - edgeCount
            ? leadingEnd + 1 // Add the next page if it's just one page away
            : null; // No divider needed if sections are adjacent or overlapping
    // Determine if we need a divider (or an extra page) between middle and trailing sections
    const trailingDivider = middleEnd < totalPages - edgeCount - 1
        ? "trailing-divider" // Add divider if there's a gap between middle and trailing sections
        : totalPages - edgeCount > edgeCount
            ? totalPages - edgeCount // Add an extra page if it's just one page away
            : null; // No divider needed if sections are adjacent or overlapping
    // Combine all parts into the final pagination array
    return [
        ...leadingPart,
        ...(!!leadingDivider ? [leadingDivider] : []), // Add leading divider if needed
        ...middlePart,
        ...(!!trailingDivider ? [trailingDivider] : []), // Add trailing divider if needed
        ...trailingPart,
    ];
}
/**
 * Checks if a pagination item is a divider.
 *
 * This is a type guard function that helps TypeScript narrow down the type
 * of a TPaginationPage to a TPaginationDividerLiteral.
 *
 * @param page - The pagination item to check.
 * @returns True if the item is a divider, false otherwise.
 */
function isDivider(page) {
    return typeof page === "string" && page.includes("divider");
}
/**
 * Creates an array of sequential numbers from start to end (inclusive).
 *
 * @param start - The first number in the range.
 * @param end - The last number in the range.
 * @returns  An array containing all integers from start to end.
 */
function range(start, end) {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export { generatePaginationPagesArray, isDivider };
